import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, Loader2, Eraser, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Página pública (sem auth) para o cliente assinar o romaneio no celular.
 * URL: /sign/:token
 */
export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [nome, setNome] = useState("");
  const [rg, setRg] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(null);
  const sigRef = useRef(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API_BASE}/api/public/romaneio/${token}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setErro(d.detail || "Link inválido"); }
        else { setData(d); if (d.assinado) setSucesso({ pre: true }); }
      })
      .catch(() => setErro("Falha de conexão"))
      .finally(() => setLoading(false));
  }, [token, API_BASE]);

  const limpar = () => { sigRef.current?.clear(); };

  const enviar = async () => {
    if (!nome.trim()) { toast.error("Nome obrigatório"); return; }
    if (sigRef.current?.isEmpty()) { toast.error("Assine no quadro abaixo"); return; }
    setSalvando(true);
    try {
      const assinatura_base64 = sigRef.current.toDataURL("image/png");
      const res = await fetch(`${API_BASE}/api/public/romaneio/${token}/assinar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome_completo: nome.trim(),
          rg: rg.trim() || null,
          assinatura_base64,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Falha");
      toast.success("Romaneio assinado com sucesso!");
      setSucesso({ protocolo: j.protocolo, assinado_em: j.assinado_em });
    } catch (e) {
      toast.error(e.message || "Falha ao assinar");
    } finally { setSalvando(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="animate-spin text-orange-600" size={32} />
      </div>
    );
  }
  if (erro) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-100">
        <Card className="max-w-md p-6 text-center">
          <div className="text-red-600 text-lg font-bold mb-2">Link inválido ou expirado</div>
          <p className="text-xs text-slate-600">{erro}</p>
          <p className="text-xs text-slate-500 mt-3">Entre em contato com a Locar Ativo Brasil para gerar um novo link.</p>
        </Card>
      </div>
    );
  }
  if (sucesso) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-emerald-50">
        <Card className="max-w-md p-6 text-center" data-testid="sign-success">
          <ShieldCheck size={56} className="mx-auto text-emerald-600 mb-3" />
          <h1 className="text-xl font-bold text-emerald-800 mb-2">Romaneio assinado!</h1>
          <p className="text-sm text-slate-600 mb-3">Obrigado! Seu romaneio foi assinado digitalmente e está disponível para a Locar Ativo Brasil.</p>
          {sucesso.protocolo && (
            <div className="bg-white rounded p-3 mb-3 text-xs">
              <div className="text-slate-500">Protocolo</div>
              <div className="font-mono font-bold">{sucesso.protocolo}</div>
            </div>
          )}
          <a
            href={`${API_BASE}/api/public/romaneio/${token}/pdf`}
            target="_blank" rel="noopener noreferrer"
            className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded text-sm font-medium"
            data-testid="sign-view-pdf"
          >
            Visualizar PDF assinado
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-3" data-testid="public-sign-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center">
          <div className="inline-block bg-orange-600 text-white px-3 py-1 rounded text-sm font-bold">LOCAR ATIVO BRASIL</div>
          <h1 className="text-2xl font-bold mt-3" style={{ color: "#0f1923" }}>Assinatura do Romaneio</h1>
          <p className="text-xs text-slate-500">Protocolo #{data.ticket_curto}</p>
        </div>

        <Card className="p-4 space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-slate-500">Cliente:</span><br /><strong>{data.cliente_nome}</strong></div>
            <div><span className="text-slate-500">CNPJ:</span><br /><strong>{data.cliente_cnpj || "—"}</strong></div>
            <div><span className="text-slate-500">Tipo:</span><br /><strong className="uppercase">{data.tipo}</strong></div>
            <div><span className="text-slate-500">Solicitante:</span><br /><strong>{data.solicitante_nome || "—"}</strong></div>
          </div>
          {data.endereco_entrega && (
            <div className="pt-2 border-t">
              <span className="text-slate-500">Endereço de entrega:</span> {data.endereco_entrega}
            </div>
          )}
          <a
            href={`${API_BASE}${data.pdf_url}`}
            target="_blank" rel="noopener noreferrer"
            className="block text-center bg-slate-100 hover:bg-slate-200 py-2 rounded text-xs font-medium mt-2"
            data-testid="sign-preview-pdf"
          >
            Ver romaneio completo (PDF)
          </a>
        </Card>

        {(data.equipamentos_retirados?.length > 0 || data.equipamentos_entregues?.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="p-3">
              <h4 className="text-xs uppercase font-bold text-orange-700 mb-2">Retirado (Cliente → LAB)</h4>
              {(data.equipamentos_retirados || []).map((e, i) => (
                <div key={i} className="text-xs font-mono mb-1">
                  <strong>{e.serial || "—"}</strong> · {e.placa || "sem placa"}
                </div>
              ))}
              {(data.equipamentos_retirados || []).length === 0 && <div className="text-xs text-slate-400">—</div>}
            </Card>
            <Card className="p-3">
              <h4 className="text-xs uppercase font-bold text-orange-700 mb-2">Entregue (LAB → Cliente)</h4>
              {(data.equipamentos_entregues || []).map((e, i) => (
                <div key={i} className="text-xs font-mono mb-1">
                  <strong>{e.serial || "—"}</strong> · {e.placa || "sem placa"}
                </div>
              ))}
              {(data.equipamentos_entregues || []).length === 0 && <div className="text-xs text-slate-400">—</div>}
            </Card>
          </div>
        )}

        <Card className="p-4 space-y-3">
          <h3 className="font-bold text-sm flex items-center gap-1">
            <CheckCircle2 size={14} className="text-emerald-600" /> Confirme seus dados e assine
          </h3>
          <div>
            <Label className="text-xs uppercase">Nome completo *</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome completo" data-testid="sign-nome" />
          </div>
          <div>
            <Label className="text-xs uppercase">RG (opcional)</Label>
            <Input value={rg} onChange={(e) => setRg(e.target.value)} placeholder="0000000 SSP-XX" data-testid="sign-rg" />
          </div>
          <div>
            <Label className="text-xs uppercase">Assinatura *</Label>
            <div className="border-2 border-dashed border-slate-300 rounded bg-white" data-testid="sign-canvas-container">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0f1923"
                canvasProps={{
                  width: 600, height: 200,
                  className: "w-full",
                  "data-testid": "sign-canvas",
                }}
              />
            </div>
            <Button size="sm" variant="outline" onClick={limpar} className="mt-2" data-testid="sign-limpar">
              <Eraser size={12} className="mr-1" /> Limpar
            </Button>
          </div>
          <Button
            className="w-full bg-orange-600 hover:bg-orange-700"
            onClick={enviar}
            disabled={salvando}
            data-testid="sign-enviar"
          >
            {salvando && <Loader2 className="animate-spin mr-2" size={14} />}
            <ShieldCheck size={14} className="mr-1" /> Confirmar assinatura
          </Button>
        </Card>
      </div>
    </div>
  );
}
